// AR scene lighting — IBL baseline + WebXR light estimation.
//
// Why this exists: wheel GLBs are metallic PBR. A metal surface has almost
// no diffuse response — its appearance comes almost entirely from the
// environment map it reflects. With only analytic AmbientLight +
// DirectionalLight and NO scene.environment, a metalness≈1 wheel renders
// near-black regardless of how bright the analytic lights (or the real
// world) are. That was the "black wheel in bright daylight" report on the
// Galaxy A36 (2026-07-15).
//
// Two layers:
//   1. Baseline IBL — a neutral RoomEnvironment PMREM set as
//      scene.environment. Zero-asset, always present, so metals always
//      reflect something. This alone fixes the black wheel.
//   2. Adaptive — WebXR light estimation via three's XREstimatedLight.
//      On ARCore devices that grant `light-estimation`, this feeds a real
//      ambient spherical-harmonic probe + a directional key light matching
//      the sun, and (when the device exposes a reflection cube map) a
//      real-world environment map. It adapts the wheel to the actual scene
//      brightness — bright daylight vs a dim room. Falls back to the
//      baseline IBL where estimation isn't available.
//
// The lighting POLICY (which env is active, how bright the manual fill is
// in each state) is the pure planLighting() below and is unit-tested. The
// GL-bound wiring (PMREM bake, XREstimatedLight) needs a real WebGL context
// and is validated on-device.

import * as THREE from 'three'
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js'
import { XREstimatedLight } from 'three/examples/jsm/webxr/XREstimatedLight.js'

/** Which environment map scene.environment should point at. */
export type EnvSource = 'baseline' | 'estimated'

export interface LightingPlan {
  readonly envSource: EnvSource
  readonly ambientIntensity: number
  readonly directionalIntensity: number
}

/**
 * Tunable AR lighting constants. `exposure` mirrors the configurator
 * (sceneConfig.ts DEFAULT_TONE_MAPPING_EXPOSURE = LIGHTING_CONFIG.exposure)
 * so the AR wheel tone-maps the same way the customer saw it in the
 * configurator. Baseline intensities are the fill used when there is no
 * WebXR light estimate; they are deliberately non-zero so a metallic wheel
 * is never black.
 */
export const AR_LIGHTING = {
  baselineAmbient: 0.9,
  baselineDirectional: 1.1,
  // Under WebXR estimation, XREstimatedLight contributes its own ambient
  // probe + directional key light, so the manual directional drops to 0 to
  // avoid double-lighting. A small ambient floor remains so a very dark
  // room never renders the wheel fully invisible.
  estimatedAmbient: 0.2,
  estimatedDirectional: 0,
  environmentIntensity: 1,
  exposure: 0.8,
} as const

/**
 * Pure lighting policy. Given whether WebXR light estimation is currently
 * active and whether the device supplied a reflection-cube environment,
 * decide which environment map is active and how bright the manual fill
 * lights should be. Never returns an "off" state — there is always an
 * environment source and (at minimum) an ambient floor, so the wheel is
 * visible in every state.
 */
export function planLighting(
  estimationActive: boolean,
  estimatedEnvAvailable: boolean,
): LightingPlan {
  if (!estimationActive) {
    return {
      envSource: 'baseline',
      ambientIntensity: AR_LIGHTING.baselineAmbient,
      directionalIntensity: AR_LIGHTING.baselineDirectional,
    }
  }
  return {
    envSource: estimatedEnvAvailable ? 'estimated' : 'baseline',
    ambientIntensity: AR_LIGHTING.estimatedAmbient,
    directionalIntensity: AR_LIGHTING.estimatedDirectional,
  }
}

/**
 * Owns the AR scene's lights + environment maps and applies the lighting
 * policy. Created with the scene (analytic lights added immediately so the
 * scene is never unlit); the GL-bound baseline env + WebXR estimation are
 * installed once the WebGLRenderer exists (installGlLighting).
 */
export class SceneLighting {
  readonly ambientLight: THREE.AmbientLight
  readonly directionalLight: THREE.DirectionalLight

  private scene: THREE.Scene
  private baselineEnv: THREE.Texture | null = null
  private estimatedLight: XREstimatedLight | null = null
  private estimatedEnvActive = false

  constructor(scene: THREE.Scene) {
    this.scene = scene
    this.ambientLight = new THREE.AmbientLight(0xffffff, AR_LIGHTING.baselineAmbient)
    this.directionalLight = new THREE.DirectionalLight(
      0xffffff,
      AR_LIGHTING.baselineDirectional,
    )
    this.directionalLight.position.set(1, 2, 1)
    this.scene.add(this.ambientLight)
    this.scene.add(this.directionalLight)
    this.scene.environmentIntensity = AR_LIGHTING.environmentIntensity
    this.applyPlan(planLighting(false, false))
  }

  /**
   * GL-bound install. MUST be called after the WebGLRenderer exists but
   * BEFORE renderer.xr.setSession(): XREstimatedLight subscribes to the
   * renderer's `sessionstart` event, which setSession fires — construct it
   * too late and the light probe is never requested.
   *
   * Bakes the neutral RoomEnvironment into a PMREM as the baseline env and
   * wires WebXR light estimation on top.
   */
  installGlLighting(renderer: THREE.WebGLRenderer): void {
    renderer.toneMapping = THREE.ACESFilmicToneMapping
    renderer.toneMappingExposure = AR_LIGHTING.exposure

    const pmrem = new THREE.PMREMGenerator(renderer)
    this.baselineEnv = pmrem.fromScene(new RoomEnvironment(), 0.04).texture
    pmrem.dispose()
    this.scene.environment = this.baselineEnv

    const xrLight = new XREstimatedLight(renderer)
    this.estimatedLight = xrLight

    xrLight.addEventListener('estimationstart', () => {
      this.scene.add(xrLight)
      this.estimatedEnvActive = xrLight.environment != null
      this.applyPlan(planLighting(true, this.estimatedEnvActive))
    })

    xrLight.addEventListener('estimationend', () => {
      this.scene.remove(xrLight)
      this.estimatedEnvActive = false
      this.applyPlan(planLighting(false, false))
    })
  }

  /** Reparent lights + active environment onto a new scene. Used by the
   *  legacy XR8 onSceneReady handoff (not exercised on the WebXR path). */
  reparentTo(scene: THREE.Scene): void {
    scene.add(this.ambientLight)
    scene.add(this.directionalLight)
    if (this.estimatedLight != null && this.estimatedLight.parent != null) {
      scene.add(this.estimatedLight)
    }
    scene.environmentIntensity = AR_LIGHTING.environmentIntensity
    this.scene = scene
    this.applyPlan(
      planLighting(this.estimatedLight?.parent != null, this.estimatedEnvActive),
    )
  }

  dispose(): void {
    this.baselineEnv?.dispose()
    // XREstimatedLight defines dispose() inline in its constructor, so the
    // jsm type doesn't declare it — call through a narrow cast.
    if (this.estimatedLight != null) {
      ;(this.estimatedLight as unknown as { dispose(): void }).dispose()
    }
  }

  private applyPlan(plan: LightingPlan): void {
    this.ambientLight.intensity = plan.ambientIntensity
    this.directionalLight.intensity = plan.directionalIntensity
    this.scene.environment =
      plan.envSource === 'estimated' && this.estimatedLight?.environment != null
        ? this.estimatedLight.environment
        : this.baselineEnv
  }
}
